import React, { Fragment, useCallback, useContext, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";

import Grid from "@material-ui/core/Grid";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";

import {
  validateCollection,
  validateCollectionField,
  hasError,
  firstError,
} from "../collectionValidation";
import MediaDialog from "../../common/MediaDialog.js";
import EditColumn from "../../common/EditColumn";
import NewThumbnailOverlay from "../../common/NewThumbnailOverlay";
import EditDescriptionColumn from "../../common/EditDescriptionColumn";
import { useSnackAlert } from "../../../common/use-snackAlert";
import {
  useCollectionCreate,
  useUploadCollectionThumbnail,
} from "../collectionServices";
import { useRouteMatch } from "react-router-dom";
import { MediaContext } from "../../common/media-context";
import { isBlank, notBlank } from "../../../util/ext";
import LoadingIndicator from "../../../components/LoadingIndicator";
import DefaultContentSort from "../edit/DefaultContentSort";
import { mergeRight } from "ramda";

const useStyles = makeStyles(theme => ({
  root: {
    padding: "0",
  },

  dialog: {
    width: "866px",
    borderRadius: "20px",
    boxShadow: "0px 0px 20px rgba(0, 0, 0, 0.15)",
  },

  close: {
    width: "40px",
    height: "40px",
    position: "relative",
    right: "-10px",
  },

  error: {
    color: theme.palette.error.main,
    paddingTop: 5,
  },

  title: {
    textAlign: "left",
    color: theme.palette.brandPrimary,
  },

  description: {
    margin: theme.spacing(6, 0, 0, 0),
    width: "100%",
  },

  loadingIndicator: {
    position: "absolute",
    height: "100%",
    width: "100%",
    backgroundColor: theme.palette.secondary.dark,
    opacity: 0.7,
  },
}));

const validator = {
  validateField: validateCollectionField,
  validate: validateCollection,
  hasError: hasError,
  firstError: firstError,
};

const initialCollection = {
  name: "",
  mediaType: "collection",
  description: "",
  descriptionText: "",
  thumbnailUrl: "",
  deafultContentSort: "",
};

const AddNewCollectionDialog = props => {
  const classes = useStyles();
  const { path } = useRouteMatch();
  const context = useContext(MediaContext);
  const { open, onSave, onClose } = props;

  const { SnackAlert, showAlert } = useSnackAlert();

  //-- Set up overall state
  const [collection, setCollection] = useState(initialCollection);
  const [thumbnailUrl, setThumbnailUrl] = useState(null);
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const createCollection = useCollectionCreate();
  const uploadThumbnail = useUploadCollectionThumbnail();
  const handleSave = useCallback(async () => {
    setLoading(true);
    const validation = mergeRight(validator.validate(collection), errors);
    try {
      if (notBlank(validation) || isBlank(thumbnailFile)) {
        if (isBlank(thumbnailFile)) {
          setErrors(
            mergeRight(validation, {
              thumbnail: ["A thumbnail must be selected"],
            }),
          );
        } else {
          setErrors(validation);
        }

        throw new Error("Please address missing or invalid values");
      }

      const newCollection = await createCollection.mutateAsync({
        data: collection,
      });

      await uploadThumbnail.mutateAsync({
        resourceIds: { id: newCollection.id },
        data: thumbnailFile,
      });

      setLoading(false);
      onSave({
        redirectPath: path,
        newCollectionId: newCollection.id,
      });
    } catch (error) {
      showAlert("error", [error.message]);
    } finally {
      setLoading(false);
    }
  }, [
    collection,
    createCollection,
    uploadThumbnail,
    thumbnailFile,
    errors,
    showAlert,
    onSave,
    path,
  ]);

  const handleClose = useCallback(() => {
    setCollection(initialCollection);
    setThumbnailUrl(null);
    setThumbnailFile(null);
    setErrors({});
    setLoading(false);
    onClose();
  }, [onClose, setLoading]);

  const formBuilder = useCallback(
    handlers => {
      return (
        <MediaDialog
          maxWidth="xl"
          fullWidth
          open={open}
          title="Add New Collection"
          handleClose={handleClose}
          loading={loading}
          content={
            <Grid container spacing={4}>
              {loading && <LoadingIndicator transparent />}
              <Grid item>
                <NewThumbnailOverlay
                  accept="jpg, jpeg, image/jpeg, image/jpg, png, image/png"
                  showAlert={showAlert}
                />
                {validator.hasError(errors, "thumbnail") && (
                  <div className={classes.error}>
                    {validator.firstError(errors, "thumbnail")}
                  </div>
                )}
              </Grid>

              <Grid container item xs spacing={4}>
                <Grid item xs>
                  <TextField
                    id="collection-name"
                    fullWidth
                    required
                    label="Title"
                    value={context.media.name}
                    onChange={handlers.onTextChange(["name"])}
                    error={hasError(errors, "name")}
                    helperText={firstError(errors, "name")}
                    onBlur={() => {
                      setErrors(
                        validateCollectionField("name", collection.name),
                      );
                    }}
                  />
                </Grid>
              </Grid>
              <Grid item xs={4}>
                <DefaultContentSort />
              </Grid>

              <Grid item xs={12}>
                <EditDescriptionColumn
                  required
                  allowHtml
                  enableLinks
                  className={classes.description}
                  error={validator.hasError(errors, "description")}
                  helperText={validator.firstError(errors, "description")}
                  onBlur={() =>
                    handlers.onBlur("description", collection.description)
                  }
                />
              </Grid>
            </Grid>
          }
          actions={
            <Button
              variant="contained"
              color="primary"
              onClick={handleSave}
              disabled={loading}
            >
              Save
            </Button>
          }
        />
      );
    },
    [
      classes,
      handleClose,
      handleSave,
      showAlert,
      context,
      open,
      collection,
      loading,
      errors,
    ],
  );

  return (
    <MediaContext.Provider
      value={{
        initial: initialCollection,
        media: collection,
        setMedia: setCollection,
        errors: errors,
        setErrors: setErrors,
        thumbnailUrl: thumbnailUrl,
        setThumbnailUrl: setThumbnailUrl,
        thumbnailFile: thumbnailFile,
        setThumbnailFile: setThumbnailFile,
      }}
    >
      <Fragment>
        <SnackAlert />
        <EditColumn
          isNew
          className={classes.root}
          formBuilder={formBuilder}
          validator={validator}
        />
      </Fragment>
    </MediaContext.Provider>
  );
};

export default AddNewCollectionDialog;
